import { supabase, isConfigured } from './supabase';

// ── AUTH ──────────────────────────────────────────────────────────────────────

export async function signUp(email, password, username) {
  if (!isConfigured) throw new Error('Supabase not configured.');
  const { data, error } = await supabase.auth.signUp({ email, password });
  if (error) throw error;
  if (data.user) {
    const { error: pe } = await supabase.from('profiles').insert({
      id: data.user.id,
      username,
      total_score: 0,
      games_played: 0,
      total_correct: 0,
      best_streak: 0,
      duel_wins: 0,
      earned_badges: [],
      civ_stats: {},
    });
    if (pe && pe.code !== '23505') throw pe; // 23505 = duplicate key, safe to ignore
  }
  return data;
}

export async function signIn(email, password) {
  if (!isConfigured) throw new Error('Supabase not configured.');
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

export async function signOut() {
  if (!isConfigured) return;
  await supabase.auth.signOut();
}

export async function getSession() {
  if (!isConfigured) return null;
  const { data } = await supabase.auth.getSession();
  return data.session;
}

// ── PROFILE ───────────────────────────────────────────────────────────────────

export async function fetchProfile(userId) {
  if (!isConfigured) return null;
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  if (error) return null;
  return data;
}

export async function upsertProfile(userId, fields) {
  if (!isConfigured) return;
  const { error } = await supabase
    .from('profiles')
    .upsert({ id: userId, ...fields, updated_at: new Date().toISOString() });
  if (error) throw error;
}

export async function ensureProfile(userId, username) {
  if (!isConfigured) return;
  const existing = await fetchProfile(userId);
  if (existing) return existing;
  await upsertProfile(userId, {
    username: username || 'Scholar',
    total_score: 0, games_played: 0, total_correct: 0,
    best_streak: 0, duel_wins: 0, earned_badges: [], civ_stats: {},
  });
  return fetchProfile(userId);
}

export async function syncLocalToBackend(userId, local) {
  if (!isConfigured) return null;
  const remote = await fetchProfile(userId);
  const merged = {
    total_score:   Math.max(remote?.total_score   || 0, local.totalScore   || 0),
    games_played:  Math.max(remote?.games_played  || 0, local.gamesPlayed  || 0),
    total_correct: Math.max(remote?.total_correct || 0, local.totalCorrect || 0),
    best_streak:   Math.max(remote?.best_streak   || 0, local.bestStreak   || 0),
    duel_wins:     Math.max(remote?.duel_wins     || 0, local.duelWins     || 0),
    earned_badges: [...new Set([...(remote?.earned_badges || []), ...(local.earnedBadges || [])])],
    civ_stats:     mergeCivStats(remote?.civ_stats || {}, local.civStats || {}),
    username:      local.username || remote?.username || 'Scholar',
  };
  await upsertProfile(userId, merged);
  return { ...remote, ...merged };
}

function mergeCivStats(remote, local) {
  const merged = { ...remote };
  for (const [civ, ls] of Object.entries(local)) {
    const rs = merged[civ] || { rounds: 0, correct: 0, total: 0, bestPct: 0 };
    merged[civ] = {
      rounds:  Math.max(rs.rounds,  ls.rounds  || 0),
      correct: Math.max(rs.correct, ls.correct || 0),
      total:   Math.max(rs.total,   ls.total   || 0),
      bestPct: Math.max(rs.bestPct, ls.bestPct || 0),
    };
  }
  return merged;
}

// ── GAMES ─────────────────────────────────────────────────────────────────────

export async function saveGame(userId, { score, correct, total, civ, diff, bestStreak }) {
  if (!isConfigured) return;
  await supabase.from('games').insert({
    user_id: userId, score, correct, total, civ, diff, best_streak: bestStreak,
  });
  const profile = await fetchProfile(userId);
  if (!profile) return;
  const cs = profile.civ_stats || {};
  const prev = cs[civ] || { rounds: 0, correct: 0, total: 0, bestPct: 0 };
  cs[civ] = {
    rounds:  prev.rounds  + 1,
    correct: prev.correct + correct,
    total:   prev.total   + total,
    bestPct: Math.max(prev.bestPct, total > 0 ? correct / total : 0),
  };
  await upsertProfile(userId, {
    total_score:   (profile.total_score   || 0) + score,
    games_played:  (profile.games_played  || 0) + 1,
    total_correct: (profile.total_correct || 0) + correct,
    best_streak:   Math.max(profile.best_streak || 0, bestStreak),
    civ_stats:     cs,
    earned_badges: computeEarnedBadges({
      gamesPlayed:  (profile.games_played  || 0) + 1,
      totalCorrect: (profile.total_correct || 0) + correct,
      totalScore:   (profile.total_score   || 0) + score,
      duelWins:     profile.duel_wins || 0,
      civStats:     cs,
      earnedBadges: profile.earned_badges || [],
    }),
  });
}

export async function fetchGameHistory(userId, limit = 10) {
  if (!isConfigured) return null;
  const { data, error } = await supabase
    .from('games')
    .select('score, correct, total, civ, diff, best_streak, created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) return null;
  return data;
}

function computeEarnedBadges(p) {
  const earned = new Set(p.earnedBadges || []);
  if (p.gamesPlayed >= 1)                            earned.add(0);
  if (p.totalCorrect >= 10)                          earned.add(1);
  if ((p.civStats?.nubia?.bestPct    || 0) >= 0.8)  earned.add(3);
  if ((p.duelWins                    || 0) >= 3)     earned.add(4);
  if ((p.civStats?.carthage?.rounds  || 0) > 0)     earned.add(5);
  if (p.totalScore >= 2000)                          earned.add(6);
  return [...earned];
}

// ── LEADERBOARD ───────────────────────────────────────────────────────────────

export async function fetchLeaderboard(limit = 50) {
  if (!isConfigured) return null;
  const { data, error } = await supabase
    .from('profiles')
    .select('username, total_score, best_streak, duel_wins, earned_badges')
    .order('total_score', { ascending: false })
    .limit(limit);
  if (error) return null;
  return data;
}

export async function countProfiles() {
  if (!isConfigured) return null;
  const { count, error } = await supabase
    .from('profiles')
    .select('*', { count: 'exact', head: true });
  if (error) return null;
  return count;
}

// ── DUELS ─────────────────────────────────────────────────────────────────────

function generateCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

export async function createDuel(hostId, hostUsername, civ, diff, questions) {
  if (!isConfigured) throw new Error('Supabase not configured.');
  let code, attempts = 0;
  while (attempts < 5) {
    code = generateCode();
    const { error } = await supabase.from('duels').insert({
      code, host_id: hostId, host_username: hostUsername,
      civ, diff, questions, status: 'waiting',
    });
    if (!error) return code;
    attempts++;
  }
  throw new Error('Could not generate a unique duel code. Please try again.');
}

export async function joinDuel(code, guestId, guestUsername) {
  if (!isConfigured) throw new Error('Supabase not configured.');
  const { data, error } = await supabase
    .from('duels')
    .select('*')
    .eq('code', code.toUpperCase())
    .eq('status', 'waiting')
    .single();
  if (error || !data) throw new Error('Duel not found or already started.');
  if (data.host_id === guestId) throw new Error("You can't join your own duel.");

  const { error: ue } = await supabase
    .from('duels')
    .update({ guest_id: guestId, guest_username: guestUsername, status: 'active' })
    .eq('id', data.id);
  if (ue) throw ue;

  return data;
}

export async function fetchDuel(code) {
  if (!isConfigured) return null;
  const { data, error } = await supabase
    .from('duels')
    .select('*')
    .eq('code', code.toUpperCase())
    .single();
  if (error) return null;
  return data;
}

export async function submitDuelScore(duelId, role, score, correct) {
  if (!isConfigured) return;
  const field = role === 'host'
    ? { host_score: score, host_correct: correct }
    : { guest_score: score, guest_correct: correct };

  const { data: duel } = await supabase
    .from('duels').select('host_score, guest_score').eq('id', duelId).single();
  const bothDone = role === 'host' ? duel?.guest_score != null : duel?.host_score != null;

  await supabase.from('duels').update({
    ...field,
    ...(bothDone ? { status: 'completed' } : {}),
  }).eq('id', duelId);
}

export async function incrementDuelWins(userId) {
  if (!isConfigured) return;
  const profile = await fetchProfile(userId);
  if (!profile) return;
  const newWins = (profile.duel_wins || 0) + 1;
  await upsertProfile(userId, {
    duel_wins: newWins,
    earned_badges: computeEarnedBadges({
      gamesPlayed:  profile.games_played  || 0,
      totalCorrect: profile.total_correct || 0,
      totalScore:   profile.total_score   || 0,
      duelWins:     newWins,
      civStats:     profile.civ_stats     || {},
      earnedBadges: profile.earned_badges || [],
    }),
  });
}

export function subscribeDuel(duelId, callback) {
  if (!isConfigured) return () => {};
  const channel = supabase
    .channel(`duel:${duelId}`)
    .on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'duels',
      filter: `id=eq.${duelId}`,
    }, payload => callback(payload.new))
    .subscribe();
  return () => supabase.removeChannel(channel);
}

export async function sendChallenge(hostId, hostUsername, challengedId, civ, diff, questions) {
  if (!isConfigured) throw new Error('Supabase not configured.');
  let code, attempts = 0;
  while (attempts < 5) {
    code = generateCode();
    const { error } = await supabase.from('duels').insert({
      code, host_id: hostId, host_username: hostUsername,
      challenged_id: challengedId, civ, diff, questions, status: 'pending',
    });
    if (!error) return code;
    attempts++;
  }
  throw new Error('Could not create challenge. Please try again.');
}

export async function acceptChallenge(duelId, guestId, guestUsername) {
  if (!isConfigured) throw new Error('Supabase not configured.');
  const { error } = await supabase
    .from('duels')
    .update({ guest_id: guestId, guest_username: guestUsername, status: 'active' })
    .eq('id', duelId)
    .eq('status', 'pending');
  if (error) throw error;
}

export async function declineChallenge(duelId) {
  if (!isConfigured) return;
  await supabase.from('duels').update({ status: 'declined' }).eq('id', duelId);
}

export async function cancelChallenge(duelId) {
  if (!isConfigured) return;
  await supabase.from('duels').delete().eq('id', duelId);
}

export async function fetchOpenDuels(limit = 10) {
  if (!isConfigured) return [];
  const { data, error } = await supabase
    .from('duels')
    .select('id, code, host_username, civ, diff, created_at')
    .eq('status', 'waiting')
    .is('guest_id', null)
    .is('challenged_id', null)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) return [];
  return data || [];
}

export function subscribeOpenDuels(onAdd, onRemove) {
  if (!isConfigured) return () => {};
  const channel = supabase
    .channel('open-duels-feed')
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'duels' },
      payload => {
        const d = payload.new;
        if (d.status === 'waiting' && !d.challenged_id) onAdd(d);
      })
    .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'duels' },
      payload => {
        if (payload.new.status !== 'waiting') onRemove(payload.new.id);
      })
    .subscribe();
  return () => supabase.removeChannel(channel);
}

export async function fetchIncomingChallenges(userId) {
  if (!isConfigured) return [];
  const { data } = await supabase
    .from('duels')
    .select('*')
    .eq('challenged_id', userId)
    .eq('status', 'pending')
    .order('created_at', { ascending: false });
  return data || [];
}

export function subscribeIncomingChallenges(userId, callback, suffix = '') {
  if (!isConfigured) return () => {};
  const channel = supabase
    .channel(`incoming:${userId}${suffix}`)
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'duels',
      filter: `challenged_id=eq.${userId}`,
    }, payload => callback(payload.new))
    .subscribe();
  return () => supabase.removeChannel(channel);
}

export function subscribeLobbyPresence(userId, username, score, onPlayersChange) {
  if (!isConfigured) return () => {};
  const channel = supabase.channel('duel-lobby', {
    config: { presence: { key: userId } },
  });
  channel
    .on('presence', { event: 'sync' }, () => {
      const state = channel.presenceState();
      const players = Object.values(state).flat().filter(p => p.userId !== userId);
      onPlayersChange(players);
    })
    .subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        await channel.track({ userId, username, score });
      }
    });
  return () => supabase.removeChannel(channel);
}

// ── QUESTION BANK ─────────────────────────────────────────────────────────────

export async function fetchQuestions(civ, diff, count, userId = null) {
  if (!isConfigured) return null;

  // Logged-in users: use RPC to get unseen questions
  if (userId) {
    const { data, error } = await supabase.rpc('get_unseen_questions', {
      p_user_id: userId,
      p_civ:     civ,
      p_diff:    diff,
      p_count:   count,
    });
    if (!error && data && data.length >= Math.min(count, 3)) return data;
  }

  // Fallback: random questions without seen filter
  let query = supabase
    .from('questions')
    .select('id, question, options, correct, category, era, fact')
    .eq('diff', diff);

  if (civ !== 'all') query = query.eq('civ', civ);

  const { data, error } = await query.limit(count * 4);
  if (error || !data || data.length === 0) return null;

  const shuffled = [...data].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

export async function markQuestionsSeen(userId, questionIds) {
  if (!isConfigured || !userId || !questionIds || questionIds.length === 0) return;
  const rows = questionIds.map(id => ({ user_id: userId, question_id: id }));
  await supabase
    .from('user_seen_questions')
    .upsert(rows, { onConflict: 'user_id,question_id', ignoreDuplicates: true });
}
